import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getDashboardSummary, getFlow, postScale } from '../lib/api';
import { useClusterFlow } from '../hooks/useClusterFlow';
import { useClusterStore } from '../stores/clusterStore';
import { ACCENT } from '../styles/tokens';
import { useBulkStore } from '../stores/bulkStore';
import { useSelectedIdStore } from '../stores/selectedIdStore';
import { usePaletteStore } from '../stores/paletteStore';
import { useKeyboardNav } from '../hooks/useKeyboardNav';
import { usePendingScale } from '../hooks/usePendingScale';
import { toast } from '../lib/toast';
import { ServiceList } from '../components/inspector/ServiceList';
import { ServiceStatsStrip } from '../components/inspector/ServiceStatsStrip';
import { ServicePanel } from '../components/inspector/ServicePanel';
import { hasVncTarget } from '../lib/vnc';
import type { ServiceInfo, TaskInfo } from '../lib/types';

export default function ServicesRoute() {
  const slug = useClusterStore(s => s.activeSlug);
  const selections = useBulkStore(s => s.selections.services);
  const toggle = useBulkStore(s => s.toggle);
  const setAll = useBulkStore(s => s.setAll);
  const clear = useBulkStore(s => s.clear);

  const enabled = slug !== 'all';
  const stream = useClusterFlow(enabled ? slug : null);
  const initial = useQuery({
    queryKey: ['flow', slug],
    queryFn: () => getFlow(slug),
    enabled,
    staleTime: 30_000,
  });
  // Mesma query do InspectorHeader (chave idêntica): o TanStack deduplica,
  // então o card de capacidade não custa uma chamada extra.
  const { data: summary } = useQuery({
    queryKey: ['dashboard', slug, ''],
    queryFn: () => getDashboardSummary(slug, ''),
    enabled,
    refetchInterval: 30_000,
  });

  const liveServices: ServiceInfo[] = useMemo(() => {
    if (stream.data?.flow?.services) return stream.data.flow.services;
    if (initial.data?.services) return initial.data.services;
    return [];
  }, [stream.data, initial.data]);

  const queryClient = useQueryClient();
  const sendScale = useCallback(
    (svc: ServiceInfo, replicas: number, from: number) =>
      postScale({ cluster: slug, service: svc.name, replicas, from }),
    [slug]
  );
  const {
    services,
    pending: pendingScale,
    scale,
  } = usePendingScale(liveServices, sendScale, {
    onConfirmed: () =>
      void queryClient.invalidateQueries({ queryKey: ['dashboard', slug] }),
    onUnconfirmed: (svc, target) =>
      toast(
        `${svc.name}: o cluster ainda não confirmou ${target} réplica(s) — confira o motivo no painel`,
        { tone: 'warn' }
      ),
  });

  const tasks: TaskInfo[] = useMemo(() => {
    if (stream.data?.flow?.tasks) return stream.data.flow.tasks;
    if (initial.data?.tasks) return initial.data.tasks;
    return [];
  }, [stream.data, initial.data]);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const ipsByService = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const t of tasks) {
      if (!t.ip) continue;
      const ips = map.get(t.serviceID);
      if (ips) ips.push(t.ip);
      else map.set(t.serviceID, [t.ip]);
    }
    return map;
  }, [tasks]);

  useEffect(() => {
    const pending = useSelectedIdStore.getState().consume('services');
    if (pending && services.some(s => s.id === pending)) {
      setSelectedId(pending);
      return;
    }
    if (
      selectedId !== null &&
      !services.some(s => s.id === selectedId)
    ) {
      setSelectedId(null);
    }
  }, [services, selectedId]);

  useKeyboardNav({
    list: services,
    selectedId,
    setSelectedId,
    section: 'services',
    onPaletteToggle: () => usePaletteStore.getState().toggle(),
  });

  useEffect(() => {
    const { setResolver, setTaskResolver } = useSelectedIdStore.getState();
    setResolver('services', ids =>
      ids
        .map(id => services.find(s => s.id === id))
        .filter((s): s is ServiceInfo => Boolean(s))
        .map(s => ({ id: s.id, name: s.name, currentReplicas: s.replicas }))
    );
    setTaskResolver('services', ids => {
      const set = new Set(ids);
      return tasks
        .filter(t => set.has(t.serviceID) && hasVncTarget(t))
        .map(t => t.taskID);
    });
    return () => {
      setResolver('services', null);
      setTaskResolver('services', null);
    };
  }, [services, tasks]);

  const selected = services.find(s => s.id === selectedId) ?? null;

  if (slug === 'all') {
    return <Navigate to="/overview" replace />;
  }

  return (
    <div className="flex flex-1 overflow-hidden">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className="flex-shrink-0 px-3 pb-2 pt-3">
          <ServiceStatsStrip
            services={services}
            accent={ACCENT}
            machinesTotal={summary?.totals.totalMachines}
            machinesUsed={summary?.totals.currentMachines}
          />
        </div>
        <ServiceList
          services={services}
          selectedId={selectedId}
          onSelect={setSelectedId}
          filter={filter}
          onFilterChange={setFilter}
          bulk={selections}
          onToggleBulk={id => toggle('services', id)}
          onToggleBulkAll={(ids, shouldSelectAll) => {
            const next = new Set(selections);
            if (shouldSelectAll) {
              ids.forEach(id => next.add(id));
              setAll('services', Array.from(next));
            } else {
              ids.forEach(id => next.delete(id));
              if (next.size === 0) clear('services');
              else setAll('services', Array.from(next));
            }
          }}
          onQuickScale={scale}
          pendingScale={pendingScale}
          ipsByService={ipsByService}
          compact
          accent={ACCENT}
        />
      </div>
      {selected && (
        <aside className="w-[460px] flex-shrink-0 overflow-hidden border-l border-border bg-surface">
          <ServicePanel
            item={selected}
            tasks={tasks}
            accent={ACCENT}
            onClose={() => setSelectedId(null)}
            onScale={(replicas, from) => scale(selected, replicas, from)}
          />
        </aside>
      )}
    </div>
  );
}
